import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import type { PresenceStatus } from '@comms/shared';
import { useStore } from '../store';

export function cx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(' ');
}

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
export function Button({
  variant = 'primary',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  ref?: Ref<HTMLButtonElement>;
}) {
  const styles: Record<Variant, string> = {
    primary: 'bg-accent hover:bg-accent-hover text-white',
    secondary: 'bg-bg-active hover:bg-bg-3 text-text-0',
    danger: 'bg-danger hover:brightness-90 text-white',
    ghost: 'hover:bg-bg-hover text-text-1',
    link: 'text-accent hover:underline px-0',
  };
  return (
    <button
      type="button"
      {...props}
      className={cx(
        'rounded px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
        styles[variant],
        className,
      )}
    />
  );
}

export function Input({
  label,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label?: string }) {
  const input = (
    <input
      {...props}
      className={cx(
        'w-full rounded bg-bg-0 px-3 py-2 text-text-0 outline-none placeholder:text-text-3 focus:ring-2 focus:ring-accent',
        className,
      )}
    />
  );
  if (!label) return input;
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-text-2">
        {label}
      </span>
      {input}
    </label>
  );
}

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title?: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const el = ref.current;
    (
      el?.querySelector<HTMLElement>('input, textarea') ??
      el?.querySelector<HTMLElement>('button:not([data-modal-close])')
    )?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 sm:p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          'safe-y flex h-full w-full flex-col bg-bg-2 shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-lg',
          wide ? 'max-w-3xl' : 'max-w-md',
        )}
      >
        {/* Outside the scrolling area, so closing is always one tap away (on a phone the
            window fills the screen and there's no backdrop to tap). */}
        <div className="flex shrink-0 items-center gap-2 pl-4 pr-1 pt-1 sm:pl-6 sm:pr-2 sm:pt-2">
          {title && (
            <h2 className="min-w-0 flex-1 truncate text-xl font-bold text-text-0">{title}</h2>
          )}
          <button
            type="button"
            data-modal-close
            aria-label="Close"
            title="Close"
            onClick={onClose}
            className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded text-text-2 hover:bg-bg-3 hover:text-text-0"
          >
            <Icon name="x" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-2 sm:px-6 sm:pb-6">
          {children}
        </div>
      </div>
    </div>
  );
}

const STATUS_COLORS: Record<string, string> = {
  online: 'bg-success',
  idle: 'bg-warning',
  dnd: 'bg-danger',
  offline: 'bg-text-3',
  invisible: 'bg-text-3',
};

export function StatusDot({ status, className }: { status: PresenceStatus; className?: string }) {
  return (
    <span
      className={cx(
        'inline-block rounded-full ring-[3px] ring-bg-1',
        STATUS_COLORS[status],
        className,
      )}
    />
  );
}

export function Avatar({
  userId,
  size = 40,
  showStatus,
}: {
  userId: string;
  size?: number;
  showStatus?: boolean;
}) {
  const user = useStore((s) => s.users[userId]);
  const presence = useStore((s) => s.presences[userId]);
  const name = user?.displayName ?? user?.username ?? '?';
  const hue = [...userId].reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 360, 0);
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      {user?.avatarUrl ? (
        <img src={user.avatarUrl} alt="" className="h-full w-full rounded-full object-cover" />
      ) : (
        <span
          className="flex h-full w-full items-center justify-center rounded-full font-semibold text-white"
          style={{ background: `hsl(${hue} 55% 45%)`, fontSize: size * 0.42 }}
        >
          {name.slice(0, 1).toUpperCase()}
        </span>
      )}
      {showStatus && (
        <span
          className="absolute -bottom-0.5 -right-0.5 flex"
          style={{ width: Math.max(10, size * 0.3), height: Math.max(10, size * 0.3) }}
        >
          <StatusDot status={presence?.status ?? 'offline'} className="h-full w-full" />
        </span>
      )}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        'inline-block h-5 w-5 animate-spin rounded-full border-2 border-text-3 border-t-transparent',
        className,
      )}
    />
  );
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  return <p className="mt-2 text-sm text-danger">{msg}</p>;
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: T; label: string }[];
  value: T;
  onChange: (t: T) => void;
}) {
  return (
    <div className="-mx-1 flex gap-1 overflow-x-auto px-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={cx(
            'shrink-0 whitespace-nowrap rounded px-2 py-1 text-sm font-medium',
            value === t.id
              ? 'bg-bg-active text-text-0'
              : 'text-text-2 hover:bg-bg-hover hover:text-text-1',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Icon({
  name,
  className = 'h-5 w-5',
}: {
  name: keyof typeof ICONS;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {ICONS[name]}
    </svg>
  );
}

const ICONS = {
  menu: <path d="M3 6h18M3 12h18M3 18h18" />,
  expand: <path d="M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5" />,
  pip: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <rect x="12" y="11" width="8" height="6" rx="1" />
    </>
  ),
  volume: <path d="M11 5 6 9H2v6h4l5 4V5zM15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14" />,
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  hash: <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />,
  calendar: (
    <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
  ),
  poll: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  sticker: (
    <path d="M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9.5L21 14.5V5a2 2 0 0 0-2-2h-3.5M14 21v-5a2 2 0 0 1 2-2h5M8 10h.01M15 10h.01M8.5 15a4 4 0 0 0 5 0" />
  ),
  thread: <path d="M5 4v9a4 4 0 0 0 4 4h10M15 13l4 4-4 4" />,
  forum: <path d="M21 11a2 2 0 0 1-2 2H9l-4 4V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2zM9 7h8M9 10h5" />,
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="M20 6 9 17l-5-5" />,
  send: <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />,
  external: (
    <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  pin: <path d="M12 17v5M9 10.76V6h6v4.76l2 3.24H7z" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  unlock: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 7.8-1.2" />
    </>
  ),
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  smile: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
    </>
  ),
  reply: <path d="M9 17 4 12l5-5M20 18v-2a4 4 0 0 0-4-4H4" />,
  edit: <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />,
  trash: <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />,
  paperclip: (
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  ),
  x: <path d="M18 6 6 18M6 6l12 12" />,
  logout: <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />,
  bell: <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />,
  chevron: <path d="m6 9 6 6 6-6" />,
  message: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  speaker: <path d="M11 5 6 9H2v6h4l5 4zM15.54 8.46a5 5 0 0 1 0 7.07" />,
  download: <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />,
  mic: (
    <>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4M8 22h8" />
    </>
  ),
  micOff: (
    <>
      <path d="m2 2 20 20M9 9v2a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23M12 19v3M8 22h8" />
    </>
  ),
  headphones: (
    <path d="M3 18v-6a9 9 0 0 1 18 0v6M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  ),
  headphonesOff: (
    <>
      <path d="m2 2 20 20" />
      <path d="M3 18v-6a9 9 0 0 1 14.5-7.1M21 12v6M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </>
  ),
  video: (
    <>
      <path d="m23 7-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" />
    </>
  ),
  monitor: (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  phone: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  phoneOff: (
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91M23 1 1 23" />
  ),
} as const;

export const COMMON_EMOJI = [
  '👍',
  '👎',
  '😂',
  '❤️',
  '🔥',
  '🎉',
  '😮',
  '😢',
  '😡',
  '🙏',
  '👀',
  '✅',
  '❌',
  '💯',
  '🤔',
  '😎',
  '🥳',
  '🙌',
  '👏',
  '💀',
  '🤝',
  '⭐',
  '🚀',
  '🍕',
  '☕',
  '🎮',
  '🎵',
  '📌',
  '⚠️',
  '😴',
  '🤣',
  '😅',
];
